import React, {Component} from 'react';
import {HighlightOff, Search} from '@mui/icons-material';
import {Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField} from '@mui/material';
import {throttle} from 'lodash';

import Table from '../../components/Table';

// Minimum characters before we hit the server in large-tenant mode. 1- or
// 2-char queries match too broadly and waste a server round-trip.
const SERVER_SEARCH_MIN_CHARS = 3;

export default class RecipientList extends Component {
  constructor(props){
    super(props);
    this.state={
      autoCompleteValue : '',
      individualList : props.individualList,
      page : 1,
      searching : false
    }
    this.autoComplete = this.autoComplete.bind(this);
    this.throttleInput = throttle(this.throttleInput.bind(this),300);
    this.userList = props.userList;
    this.newUserList = this.userList;
    // Monotonic counter — used to discard stale server-search responses
    // arriving out of order or after the user has cleared the field.
    this.searchSeq = 0;
  }
  componentDidUpdate(prevProps){
    // In large-tenant mode the parent dispatches search results into Redux
    // and re-renders us with a new `searchResults` reference. When that
    // happens, we know the in-flight request landed — stop the spinner.
    if (this.props.largeTenantMode && prevProps.searchResults !== this.props.searchResults && this.state.searching) {
      this.setState({searching: false});
    }
  }
  autoComplete = (e) => {
    this.setState({autoCompleteValue:e.target.value});
    this.throttleInput(e.target.value);
  }
  throttleInput = (val) => {
    const trimmed = (val || '').trim();
    // Large-tenant mode: the loaded userList is only the first page (~200),
    // so client-side filter would silently hide everything past the cutoff.
    // Hit the server instead. Below SERVER_SEARCH_MIN_CHARS we render
    // nothing rather than spam the server with broad queries.
    if (this.props.largeTenantMode && this.props.onSearch) {
      this.searchSeq++;
      if (trimmed.length < SERVER_SEARCH_MIN_CHARS) {
        this.setState({searching: false});
        return;
      }
      this.setState({searching: true});
      this.props.onSearch(trimmed);
      return;
    }
    // Small-tenant: full list is in memory, filter locally.
    this.newUserList = this.userList.filter(data => {return (data.name.toLowerCase().indexOf(val.toLowerCase()) > -1 || data.mobile.indexOf(val) > -1);});
    this.setState(prev => ({...prev}));
  }
  removeAutoCompleteValue = (e) => {
    e.stopPropagation()
    // Invalidate any in-flight server-search so its late response can't
    // repaint the (now empty) input with stale results.
    this.searchSeq++;
    if (this.props.largeTenantMode && this.props.onSearch) {
      // Tell the parent to clear Redux's search results so the next open of
      // the dialog doesn't show whatever the previous session searched for.
      this.props.onSearch('');
    }
    this.setState({autoCompleteValue : '', page : 1, searching: false});
  }
  handleCheckBox = (id, details) => {
    let {individualList} = this.state;
    if(individualList[id]){
      delete individualList[id];
    } else{
      individualList[id] = details;
    }
    this.setState({individualList});
  }
  handleAllCheckbox = () => {
    let {individualList} = this.state;
    if(this.userList.length === Object.keys(individualList).length){
      individualList = {}
    } else{
      for(let gc of this.userList){
        if(!individualList[gc.id]){
          individualList[gc.id] = gc;
        }
      }
    }
    this.setState({individualList});
  }
  render(){
    let {individualList,autoCompleteValue,searching} = this.state;
    const trimmed = (autoCompleteValue || '').trim();
    // Large-tenant rendering:
    //   - empty / sub-min query → show nothing (deliberately do not
    //     materialize the full tenant in the browser)
    //   - typed query ≥ min chars → show whatever Redux currently holds
    //     for this search (parent passes the array as `searchResults`)
    let nameList;
    if (this.props.largeTenantMode) {
      nameList = trimmed.length >= SERVER_SEARCH_MIN_CHARS ? (this.props.searchResults || []) : [];
    } else {
      nameList = autoCompleteValue == '' ? this.userList : this.newUserList;
    }
    const isMobile = window.isMobile();
    const showHint = this.props.largeTenantMode && trimmed.length > 0 && trimmed.length < SERVER_SEARCH_MIN_CHARS;
    return(
      <Dialog open onClose={this.props.handleRecipientListDialogCloseWithNoAction}>
        <DialogTitle>All Contacts List</DialogTitle>
        <DialogContent dividers style={{paddingTop: 16}}>
          {
            this.userList.length || this.props.largeTenantMode ?
              <div>
                <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                  <Search onClick={e => e.stopPropagation()} />
                  <TextField
                    label={isMobile ? "Search By Name" : "Search Contact By Name/Number"}
                    fullWidth={true}
                    value={autoCompleteValue}
                    onChange={this.autoComplete.bind(this)}
                    size="small"
                    variant="standard"
                    sx={{width: isMobile ? 300 : 600}}
                  />
                  {searching ? <CircularProgress size={20} aria-label="Searching" /> : null}
                  {
                    autoCompleteValue ?
                      <IconButton aria-label='Clear' onClick={this.removeAutoCompleteValue}>
                        <HighlightOff />
                      </IconButton>
                      : null
                  }
                </div>
                {
                  showHint ?
                    <p style={{paddingTop: 12, fontSize: 13, color: '#666'}}>Type at least {SERVER_SEARCH_MIN_CHARS} characters to search…</p>
                    :
                    nameList.length ?
                      <Table
                        column={[
                          {name: 'Name', value: 'name'},
                          {name: 'Mobile', value: 'mobile'}
                        ]}
                        data={nameList}
                        enableCheckbox
                        handleSelect={this.handleCheckBox}
                        selected={Object.keys(individualList)}
                        onSelectAllClick={this.handleAllCheckbox}
                        showPagination
                        pageOptions={[10]}
                      />
                      :
                      <p style={{paddingTop:80,fontSize:16}}>{
                        this.props.largeTenantMode && trimmed.length >= SERVER_SEARCH_MIN_CHARS && !searching
                          ? 'No Contact Available with searched keyword!'
                          : this.props.largeTenantMode
                            ? 'Type to search your contacts.'
                            : 'No Contact Available with searched keyword!'
                      }</p>
                }
              </div>
              : <p>No Contacts Available!</p>
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={this.props.handleRecipientListDialogCloseWithNoAction}>
            Cancel
          </Button>
          <Button onClick={() => this.props.handleRecipientListDialogClose(individualList)} variant='contained'>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    )
  }
}
